// chapters/Chapter1/Chapter1Lesson1.js - Web version with complete mobile functionality
import React, { useState } from 'react';
import { ArrowLeft, Brain, Check, X, RotateCcw } from 'lucide-react';
import { Button, Card } from '../../components/ui';

const API_BASE_URL = 'https://growthsprouts-server.vercel.app';

export default function Chapter1Lesson1({ chapterId, lessonId, child, family, onNavigate }) {
  const currentChapterId = chapterId || '1';
  const [currentSection, setCurrentSection] = useState(0);
  const [completedActivities, setCompletedActivities] = useState({});
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [droppedItems, setDroppedItems] = useState({
    'growth': [],
    'fixed': []
  });
  const [clickedOptions, setClickedOptions] = useState({});
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);

  // Debug logging
  console.log('🎯 Chapter1Lesson1 loaded with props:', { 
    chapterId, 
    lessonId, 
    child: child?.id, 
    family: family?.id 
  });

  const sections = [
    {
      type: "intro",
      title: "Understanding Money Mindset",
      content: {
        subtitle: "What shapes how we think about money?",
        text: "Your money mindset is like a lens through which you see all financial decisions. It's shaped by your experiences, family background, and beliefs about money.",
        icon: "🧠"
      }
    },
    {
      type: "story",
      title: "Meet Sarah & Jake",
      content: {
        story: [
          {
            character: "Sarah",
            mindset: "Growth Mindset",
            thought: "I can learn to manage money better",
            action: "Started tracking expenses and learning about budgeting",
            result: "Built an emergency fund in 6 months",
            icon: "👩‍💼",
            color: "growth"
          },
          {
            character: "Jake",
            mindset: "Fixed Mindset",
            thought: "I'm just bad with money",
            action: "Avoided looking at bank statements",
            result: "Accumulated debt and felt stressed",
            icon: "👨‍💻", 
            color: "fixed"
          }
        ]
      }
    },
    {
      type: "activity",
      title: "Mindset Check",
      content: {
        question: "Which mindset statement resonates with you?",
        options: [
          { 
            id: "growth1", 
            text: "I can improve my financial situation with effort and learning",
            type: "growth",
            feedback: "Great! This growth mindset will help you build strong financial habits."
          },
          { 
            id: "fixed1", 
            text: "Some people are just naturally good with money, others aren't",
            type: "fixed",
            feedback: "This fixed mindset might limit your financial growth. Consider how you can develop money skills!"
          },
          { 
            id: "growth2", 
            text: "Making mistakes with money is part of learning",
            type: "growth",
            feedback: "Excellent! Learning from mistakes is key to financial growth."
          },
          { 
            id: "fixed2", 
            text: "I'll never be good at managing money",
            type: "fixed",
            feedback: "This belief can become self-fulfilling. Try shifting to a growth mindset!"
          }
        ]
      }
    },
    {
      type: "drag-drop",
      title: "Sort the Mindsets",
      content: {
        instructions: "Drag each statement to the correct mindset category",
        categories: ["growth", "fixed"],
        items: [
          { id: "item1", text: "I can learn to budget better", category: "growth" },
          { id: "item2", text: "I'm bad with numbers", category: "fixed" },
          { id: "item3", text: "Financial skills can be developed", category: "growth" },
          { id: "item4", text: "Rich people are just lucky", category: "fixed" },
          { id: "item5", text: "I can improve my credit score", category: "growth" },
          { id: "item6", text: "Saving money is impossible for me", category: "fixed" }
        ]
      }
    },
    {
      type: "reflection",
      title: "Your Money Mindset Journey",
      content: {
        prompt: "How will you apply a growth mindset to your financial goals?",
        reflection: "Remember: Your mindset shapes your financial future. Embrace challenges, learn from setbacks, and believe in your ability to grow!"
      }
    }
  ];

  // Handle option clicks for activities
  const handleOptionClick = (sectionIndex, optionId) => {
    const key = `${sectionIndex}-${optionId}`;
    setClickedOptions({
      ...clickedOptions,
      [key]: true
    });

    // Mark section as completed after clicking any option
    setCompletedActivities({
      ...completedActivities,
      [sectionIndex]: true
    });
  };

  // Handle drag and drop functionality (simplified for web)
  const handleItemAssignment = (itemId, category) => {
    const item = sections[currentSection].content.items.find(item => item.id === itemId);
    
    if (item) {
      const newDroppedItems = { ...droppedItems };
      
      // Remove item from other categories
      Object.keys(newDroppedItems).forEach(cat => {
        newDroppedItems[cat] = newDroppedItems[cat]?.filter(droppedItem => droppedItem.id !== itemId) || [];
      });
      
      // Add item to new category
      newDroppedItems[category] = [...(newDroppedItems[category] || []), item];
      setDroppedItems(newDroppedItems);
      
      // Check completion
      const allItems = sections[currentSection].content.items;
      const totalDropped = Object.values(newDroppedItems).flat().length;
      
      if (totalDropped === allItems.length) {
        const allCorrect = Object.entries(newDroppedItems)
          .every(([cat, items]) => items.every(item => item.category === cat));
        
        if (allCorrect) {
          setCompletedActivities({
            ...completedActivities,
            [currentSection]: true
          });
        }
      }
    }
  };

  // Get available items for assignment
  const getAvailableItems = () => {
    const allDroppedItems = Object.values(droppedItems).flat();
    return sections[currentSection].content.items.filter(
      item => !allDroppedItems.find(dropped => dropped.id === item.id)
    );
  };

  // Complete lesson function (API call)
  const completeLesson = async () => {
    try {
      console.log('🎯 Starting lesson completion...', {
        childId: child?.id,
        familyId: family?.id,
        chapterId: currentChapterId,
        lessonId: 1
      });

      if (!child || !family) {
        console.error('❌ Missing child or family data');
        alert('Error: Missing user data');
        return;
      }

      const response = await fetch(`${API_BASE_URL}/api/lessons/complete`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json' 
        },
        body: JSON.stringify({
          child_id: child.id,
          family_id: family.id,
          chapter_id: parseInt(currentChapterId),
          lesson_id: 1
        })
      });

      console.log('🌐 API response status:', response.status);
      
      const result = await response.json();
      console.log('📊 API response data:', result);
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to save progress');
      }
      
      console.log('✅ Lesson completed successfully!');
      return result;
      
    } catch (error) {
      console.error('❌ Error completing lesson:', error);
      alert('Failed to save progress. Please try again.');
      throw error;
    }
  };

  // Navigation functions
  const handleBackToChapter = () => {
    if (onNavigate) {
      onNavigate('financialLiteracyChapter', { chapterId: currentChapterId });
    }
  };

const handleCompleteLesson = async () => {
  try {
    console.log('🚀 Completing lesson...');
    await completeLesson();
    console.log('🏁 Lesson completion saved!');
    
    // Show success message
    setShowSuccessMessage(true);
    
    // Navigate back after showing message
    setTimeout(() => {
      handleBackToChapter();
    }, 2000);
    
  } catch (error) {
    console.error('❌ Failed to complete lesson:', error);
    // Don't navigate if completion failed
  }
};

  const isLastSection = currentSection === sections.length - 1;
  
  // Check if user can proceed to next section
  const canProceed = () => {
    if (sections[currentSection].type === "intro" || sections[currentSection].type === "story" || sections[currentSection].type === "reflection") {
      return true;
    }
    if (sections[currentSection].type === "drag-drop") {
      const allItems = sections[currentSection].content.items;
      const totalDropped = Object.values(droppedItems).flat().length;
      const allCorrect = Object.entries(droppedItems)
        .every(([cat, items]) => items.every(item => item.category === cat));
      
      return totalDropped === allItems.length && allCorrect;
    }
    return completedActivities[currentSection];
  };

  // Render different section types
  const renderSection = () => {
    const section = sections[currentSection];

    switch (section.type) {
      case "intro":
        return (
          <div className="text-center space-y-6 max-w-3xl mx-auto">
            <div className="text-6xl mb-4">{section.content.icon}</div>
            <h2 className="text-3xl font-bold text-green-800 mb-4">{section.content.subtitle}</h2>
            <p className="text-xl text-gray-700 leading-relaxed">
              {section.content.text}
            </p>
          </div>
        );

      case "story":
        return (
          <div className="space-y-8 max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-green-800 text-center mb-8">
              Two Different Approaches
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              {section.content.story.map((character, idx) => (
                <Card key={idx} className={`p-6 border-2 ${
                  character.color === 'growth' 
                    ? 'border-green-200 bg-green-50' 
                    : 'border-red-200 bg-red-50'
                }`}>
                  <div className="flex items-center space-x-3 mb-4">
                    <span className="text-4xl">{character.icon}</span>
                    <div>
                      <h3 className="text-xl font-bold text-gray-800">{character.character}</h3>
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                        character.mindset.includes("Growth") 
                          ? "bg-green-100 text-green-800" 
                          : "bg-red-100 text-red-800"
                      }`}>
                        {character.mindset}
                      </span>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div>
                      <strong className="text-gray-700">Thought:</strong>
                      <p className="italic text-gray-600 mt-1">"{character.thought}"</p>
                    </div>
                    <div>
                      <strong className="text-gray-700">Action:</strong>
                      <p className="text-gray-600 mt-1">{character.action}</p>
                    </div>
                    <div>
                      <strong className="text-gray-700">Result:</strong>
                      <p className="text-gray-600 mt-1">{character.result}</p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        );

      case "activity":
        return (
          <div className="space-y-8 max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold text-green-800 text-center">
              {section.content.question}
            </h2>
            <p className="text-center text-gray-600 mb-6">
              Click on each option to see the feedback and explore different mindsets.
            </p>
            <div className="grid gap-4">
              {section.content.options.map((option, idx) => (
                <Card 
                  key={idx} 
                  className={`p-4 cursor-pointer transition-all duration-200 ${
                    clickedOptions[`${currentSection}-${option.id}`]
                      ? option.type === 'growth' 
                        ? 'border-green-400 bg-green-50' 
                        : 'border-red-400 bg-red-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  onClick={() => handleOptionClick(currentSection, option.id)}
                >
                  <div className="flex items-start space-x-3">
                    <div className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center ${
                      option.type === 'growth' ? 'bg-green-100' : 'bg-red-100'
                    }`}>
                      {option.type === 'growth' ? '✓' : '✗'}
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-gray-800 mb-2">{option.text}</p>
                      {clickedOptions[`${currentSection}-${option.id}`] && (
                        <div className={`p-3 rounded-lg ${
                          option.type === 'growth' ? 'bg-green-100' : 'bg-red-100'
                        }`}>
                          <p className="text-sm">{option.feedback}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        );

      case "drag-drop":
        return (
          <div className="space-y-8 max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-green-800 text-center">
              {section.title}
            </h2>
            <p className="text-center text-gray-600">{section.content.instructions}</p>
            
            {/* Available Items */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-800">Available Statements:</h3>
              <div className="grid gap-3">
                {getAvailableItems().map((item) => (
                  <Card key={item.id} className="p-4 bg-gray-50 border-dashed">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-700">{item.text}</span>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          className="bg-green-100 hover:bg-green-200 border-green-300"
                          onClick={() => handleItemAssignment(item.id, 'growth')}
                        >
                          Growth
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="bg-red-100 hover:bg-red-200 border-red-300"
                          onClick={() => handleItemAssignment(item.id, 'fixed')}
                        >
                          Fixed
                        </Button>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </div>

            {/* Drop Zones */}
            <div className="grid md:grid-cols-2 gap-6">
              {/* Growth Mindset */}
              <Card className="p-6 border-2 border-green-200 bg-green-50">
                <h4 className="text-xl font-bold text-green-800 mb-4 text-center">
                  🌱 Growth Mindset
                </h4>
                <div className="space-y-2 min-h-32">
                  {droppedItems.growth.map((item) => (
                    <div key={item.id} className="p-3 bg-white rounded border border-green-200">
                      <div className="flex justify-between items-center">
                        <span className="text-sm">{item.text}</span>
                        <button
                          onClick={() => {
                            const newDropped = { ...droppedItems };
                            newDropped.growth = newDropped.growth.filter(i => i.id !== item.id);
                            setDroppedItems(newDropped);
                          }}
                          className="text-red-500 hover:text-red-700 text-sm"
                        >
                          ✗
                        </button>
                      </div>
                      {item.category === 'growth' && (
                        <div className="mt-1 text-xs text-green-600">✓ Correct!</div>
                      )}
                      {item.category !== 'growth' && (
                        <div className="mt-1 text-xs text-red-600">✗ Try the other category</div>
                      )}
                    </div>
                  ))}
                </div>
              </Card>

              {/* Fixed Mindset */}
              <Card className="p-6 border-2 border-red-200 bg-red-50">
                <h4 className="text-xl font-bold text-red-800 mb-4 text-center">
                  🔒 Fixed Mindset
                </h4>
                <div className="space-y-2 min-h-32">
                  {droppedItems.fixed.map((item) => (
                    <div key={item.id} className="p-3 bg-white rounded border border-red-200">
                      <div className="flex justify-between items-center">
                        <span className="text-sm">{item.text}</span>
                        <button
                          onClick={() => {
                            const newDropped = { ...droppedItems };
                            newDropped.fixed = newDropped.fixed.filter(i => i.id !== item.id);
                            setDroppedItems(newDropped);
                          }}
                          className="text-red-500 hover:text-red-700 text-sm"
                        >
                          ✗
                        </button>
                      </div>
                      {item.category === 'fixed' && (
                        <div className="mt-1 text-xs text-green-600">✓ Correct!</div>
                      )}
                      {item.category !== 'fixed' && (
                        <div className="mt-1 text-xs text-red-600">✗ Try the other category</div>
                      )}
                    </div>
                  ))}
                </div>
              </Card>
            </div>
          </div>
        );

      case "reflection":
        return (
          <div className="text-center space-y-6 max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold text-green-800">{section.title}</h2>
            <div className="bg-green-50 p-6 rounded-lg border border-green-200">
              <p className="text-lg text-green-800 font-medium mb-4">{section.content.prompt}</p>
              <p className="text-gray-700 leading-relaxed">{section.content.reflection}</p>
            </div>
            <div className="text-4xl">🌟</div>
          </div>
        );

      default:
        return <div>Unknown section type</div>;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-green-50">
      {showSuccessMessage && (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8 max-w-md mx-4 text-center shadow-xl">
          <div className="text-6xl mb-4">🎉</div>
          <h2 className="text-2xl font-bold text-green-800 mb-2">Congratulations!</h2>
          <p className="text-gray-600 mb-4">
            You've completed "Understanding Money Mindset"
          </p>
          <div className="text-green-600 font-medium">
            Returning to chapter overview...
          </div>
          <div className="mt-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-500 mx-auto"></div>
          </div>
        </div>
      </div>
    )}
      <div className="max-w-6xl mx-auto px-6 py-8">
        
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <Button
            variant="ghost"
            onClick={handleBackToChapter}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-800"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Chapter
          </Button>
          <div className="text-sm text-gray-500">
            Section {currentSection + 1} of {sections.length}
          </div>
        </div>

        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex justify-between text-sm text-gray-600 mb-2">
            <span>Lesson Progress</span>
            <span>{Math.round(((currentSection + 1) / sections.length) * 100)}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-gradient-to-r from-blue-500 to-green-500 h-2 rounded-full transition-all duration-300"
              style={{ width: `${((currentSection + 1) / sections.length) * 100}%` }}
            ></div>
          </div>
        </div>

        {/* Lesson Title */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Brain className="w-8 h-8 text-green-600" />
            <h1 className="text-4xl font-bold text-gray-800">Understanding Money Mindset</h1>
          </div>
          <p className="text-lg text-gray-600">Learn how your mindset shapes your financial future</p>
        </div>

        {/* Section Content */}
        <div className="mb-12">
          {renderSection()}
        </div>

        {/* Navigation */}
        <div className="flex justify-between items-center">
          <Button
            variant="outline"
            onClick={() => setCurrentSection(Math.max(0, currentSection - 1))}
            disabled={currentSection === 0}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Previous
          </Button>

          <div className="flex items-center gap-2">
            {sections.map((_, index) => (
              <div
                key={index}
                className={`w-3 h-3 rounded-full ${
                  index === currentSection
                    ? 'bg-green-500'
                    : index < currentSection
                    ? 'bg-green-300'
                    : 'bg-gray-200'
                }`}
              />
            ))}
          </div>

          {isLastSection ? (
            <Button
              onClick={handleCompleteLesson}
              disabled={!canProceed()}
              className="bg-green-600 hover:bg-green-700 text-white flex items-center gap-2"
            >
              <Check className="w-4 h-4" />
              Complete Lesson
            </Button>
          ) : (
            <Button
              onClick={() => setCurrentSection(currentSection + 1)}
              disabled={!canProceed()}
              className="bg-blue-600 hover:bg-blue-700 text-white flex items-center gap-2"
            >
              Next
              <ArrowLeft className="w-4 h-4 rotate-180" />
            </Button>
          )}
        </div>

        {/* Completion Status */}
        {!canProceed() && sections[currentSection].type !== "intro" && sections[currentSection].type !== "story" && sections[currentSection].type !== "reflection" && (
          <div className="mt-4 text-center">
            <p className="text-amber-600 text-sm">
              {sections[currentSection].type === "drag-drop" 
                ? "Place all items correctly to continue"
                : "Complete the activity to continue"
              }
            </p>
          </div>
        )}
      </div>
    </div>
  );
}